import { Link } from 'react-router-dom';
import type { Manga } from '@/mocks/manga';

interface MangaCardProps {
  manga: Manga;
  variant?: 'default' | 'featured' | 'compact';
}

export default function MangaCard({ manga, variant = 'default' }: MangaCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${manga.id}`}
        className="group block cursor-pointer"
      >
        <div className="flex gap-3 p-2 rounded-lg hover:bg-background-100 transition-colors">
          <div className="w-16 h-20 flex-shrink-0 rounded-md overflow-hidden">
            <img
              src={manga.cover}
              alt={manga.title}
              title={`${manga.title} - 178漫画网`}
              className="w-full h-full object-cover object-top"
              loading="lazy"
            />
          </div>
          <div className="flex flex-col justify-center min-w-0">
            <h4 className="text-sm font-medium text-foreground-800 truncate group-hover:text-primary-600 transition-colors">
              {manga.title}
            </h4>
            <p className="text-xs text-foreground-500 mt-1">{manga.author}</p>
            <p className="text-xs text-foreground-400 mt-0.5">{manga.latestChapter}</p>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${manga.id}`}
        className="group block cursor-pointer"
      >
        <div className="relative rounded-xl overflow-hidden aspect-[3/4]">
          <img
            src={manga.cover}
            alt={manga.title}
            title={`${manga.title} - 178漫画网`}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <h3 className="text-lg font-bold text-white mb-1">{manga.title}</h3>
            <p className="text-xs text-white/70 line-clamp-2 mb-2">{manga.description}</p>
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 bg-accent-500 text-white text-xs rounded-full whitespace-nowrap">
                {manga.status}
              </span>
              <span className="text-xs text-white/60">{manga.latestChapter}</span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${manga.id}`}
      className="group block cursor-pointer"
    >
      <div className="bg-background-50 rounded-xl overflow-hidden transition-colors hover:bg-background-100">
        <div className="aspect-[3/4] overflow-hidden">
          <img
            src={manga.cover}
            alt={manga.title}
            title={`${manga.title} - 178漫画网`}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        </div>
        <div className="p-3">
          <div className="flex items-center justify-between mb-1">
            <h4 className="text-sm font-semibold text-foreground-800 truncate group-hover:text-primary-600 transition-colors">
              {manga.title}
            </h4>
            {manga.isNew && (
              <span className="px-1.5 py-0.5 bg-accent-100 text-accent-700 text-xs rounded-full whitespace-nowrap font-medium">
                NEW
              </span>
            )}
          </div>
          <p className="text-xs text-foreground-500 mb-1.5">{manga.author}</p>
          <div className="flex items-center justify-between">
            <span className="text-xs text-foreground-400 truncate max-w-[60%]">
              {manga.latestChapter}
            </span>
            <span className="flex items-center gap-0.5 text-xs text-accent-600">
              <i className="ri-star-fill text-xs"></i>
              {manga.rating}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}